import React, { useEffect, useMemo, useState, FunctionComponent, useRef } from 'react';
import styled, { StyledComponent, css } from 'styled-components';
import ReactQuill from 'react-quill';
import { Theme, Colors, AnalyticsProps } from '@inplayer-org/inplayer-ui';

// editor styles
import 'react-quill/dist/quill.snow.css';

// utils
import throttle from 'lodash/throttle';

interface Props extends AnalyticsProps {
  value: string | any;
  readOnly?: boolean;
  onChange?: (e: any) => any;
  displayToolbar?: boolean;
  hideToolbarLink?: boolean;
  placeholder?: string;
  customModules?: any;
  onBlur?: () => void;
  toolbarOptions?: Array<string>;
}

interface StyledEditorContainerProps {
  readOnly?: boolean;
  hideToolbarLink?: boolean;
}

const EditorContainer: StyledComponent<'div', Theme, StyledEditorContainerProps> = styled.div`
  width: 100%;

  .ql-editor p,
  .ql-editor ol,
  .ql-editor ul,
  .ql-editor pre,
  .ql-editor blockquote,
  .ql-editor h1,
  .ql-editor h2,
  .ql-editor h3,
  .ql-editor h4,
  .ql-editor h5,
  .ql-editor h6 {
    font-family: Roboto;
    font-weight: ${({ theme }) => theme.font.weights.light};
    line-height: 1;
    font-size: ${({ theme }) => theme.font.sizes.medium};
    color: ${Colors.fontDarkGray};
  }

  .ql-tooltip,
  .ql-tooltip.ql-editing {
    left: 0 !important;
    z-index: 1;
  }

  ${({ hideToolbarLink }: StyledEditorContainerProps) =>
    hideToolbarLink &&
    css`
      button.ql-link {
        display: none !important;
      }
    `}

  ${({ readOnly }: StyledEditorContainerProps) =>
    readOnly &&
    css`
      .ql-container.ql-snow {
        border: 0;
      }

      .ql-editor {
        padding-left: 0;
      }

      .ql-editor p,
      .ql-editor ol,
      .ql-editor ul,
      .ql-editor pre,
      .ql-editor blockquote,
      .ql-editor h1,
      .ql-editor h2,
      .ql-editor h3,
      .ql-editor h4,
      .ql-editor h5,
      .ql-editor h6 {
        font-weight: ${({ theme }) => theme.font.weights.normal};
        color: ${Colors.black};
        padding: 0.5em;
        background: transparent;
      }
    `}
`;

const TextEditor: FunctionComponent<Props> = ({
  value,
  onChange,
  readOnly,
  hideToolbarLink,
  displayToolbar = true,
  placeholder,
  customModules,
  onBlur,
  toolbarOptions,
}) => {
  const [editorState, setEditorState] = useState(value);

  useEffect(() => {
    setEditorState(value);
  }, [value]);

  const handleChange = useMemo(
    () =>
      throttle((val: string) => {
        setEditorState(val);
        if (onChange) onChange(val);
      }, 500),
    []
  );

  const modules = React.useMemo(() => {
    let defaultModules = {
      toolbar: toolbarOptions || displayToolbar,
    };

    if (customModules) {
      defaultModules = {
        ...defaultModules,
        ...customModules,
      };
    }
    return defaultModules;
  }, []);

  const onEditorBlur = () => {
    if (onBlur) onBlur();
  };

  return (
    <EditorContainer readOnly={readOnly} hideToolbarLink={hideToolbarLink}>
      <ReactQuill
        value={editorState || ''}
        onChange={handleChange}
        readOnly={readOnly}
        modules={modules}
        placeholder={placeholder}
        onBlur={onEditorBlur}
      />
    </EditorContainer>
  );
};

export default TextEditor;
